import { GatewaySettings } from '@/features/commerce/commerce';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, RefreshCw } from 'lucide-react';
import { PageHeader, TableSkeleton } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select } from '@/components/ui/misc';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { FormError } from '@/features/auth/auth-layout';
import { ApiRequestError } from '@/lib/api';
import { CURRENCIES } from '@/lib/currencies';
import { formatDate, formatDateTime, formatMoney } from '@/lib/format';
import { useSession } from '@/features/auth/use-session';
import {
  useBankAccounts,
  useChartAccounts,
  useExchangeRates,
  useExpenseCategories,
  useRateQuote,
  useRefreshExchangeRates,
  useSaveBankAccount,
  useSaveTaxRate,
  useSaveVendor,
  useTaxRates,
  useVendors,
} from './api';
import { TAX_TREATMENT_LABELS } from './labels';
import type { TaxTreatment } from './types';

const TABS = [
  ['tax', 'Tax rates'],
  ['banks', 'Bank accounts'],
  ['accounts', 'Chart of accounts'],
  ['vendors', 'Vendors'],
  ['currencies', 'Currencies'],
  ['payments', 'Online payments'],
] as const;

export function FinanceSettingsPage() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as (typeof TABS)[number][0]) ?? 'tax';

  return (
    <div>
      <PageHeader
        title="Finance settings"
        description="Tax, banking and the chart of accounts every posting rule uses."
      />

      <div className="mb-5 flex gap-1 border-b">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            onClick={() => setParams({ tab: key })}
            className={
              tab === key
                ? 'border-b-2 border-primary px-3 py-2 text-sm font-medium'
                : 'px-3 py-2 text-sm text-muted-foreground hover:text-foreground'
            }
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'tax' && <TaxRatesTab />}
      {tab === 'banks' && <BankAccountsTab />}
      {tab === 'accounts' && <ChartAccountsTab />}
      {tab === 'vendors' && <VendorsTab />}
      {tab === 'currencies' && <CurrenciesTab />}
      {tab === 'payments' && <GatewaySettings scope="workspace" />}
    </div>
  );
}

function TaxRatesTab() {
  const { data: rates = [], isLoading } = useTaxRates();
  const save = useSaveTaxRate();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', ratePercent: 0, treatment: 'STANDARD' as TaxTreatment });
  const [error, setError] = useState<string | null>(null);

  if (isLoading) return <TableSkeleton columns={4} />;

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between pb-3">
        <div>
          <CardTitle className="text-base">Tax rates</CardTitle>
          <CardDescription>
            An invoice freezes the percentage it was raised at, so changing a rate never rewrites
            history.
          </CardDescription>
        </div>
        <Can permission="finance.settings.update">
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" />
            Add rate
          </Button>
        </Can>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Rate</TableHead>
              <TableHead>Treatment</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rates.map((rate) => (
              <TableRow key={rate.id}>
                <TableCell className="font-medium">{rate.name}</TableCell>
                <TableCell className="tabular">{rate.ratePercent}%</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {TAX_TREATMENT_LABELS[rate.treatment]}
                </TableCell>
                <TableCell className="text-end">
                  {rate.isDefault && <Badge variant="default">Default</Badge>}
                  {!rate.isActive && <Badge variant="secondary">Retired</Badge>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {adding && (
          <div className="mt-4 space-y-3 rounded-md border p-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input
                  value={form.name}
                  placeholder="VAT 5%"
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Rate %</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step="0.01"
                  value={form.ratePercent}
                  onChange={(event) =>
                    setForm({ ...form, ratePercent: Number(event.target.value) })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label>Treatment</Label>
                <Select
                  value={form.treatment}
                  onChange={(event) =>
                    setForm({ ...form, treatment: event.target.value as TaxTreatment })
                  }
                >
                  {(Object.keys(TAX_TREATMENT_LABELS) as TaxTreatment[]).map((value) => (
                    <option key={value} value={value}>
                      {TAX_TREATMENT_LABELS[value]}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            <FormError message={error} />
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={save.isPending || form.name.trim().length === 0}
                onClick={async () => {
                  setError(null);
                  try {
                    await save.mutateAsync(form);
                    setAdding(false);
                    setForm({ name: '', ratePercent: 0, treatment: 'STANDARD' });
                  } catch (caught) {
                    setError(
                      caught instanceof ApiRequestError ? caught.message : 'Could not save.',
                    );
                  }
                }}
              >
                Save rate
              </Button>
              <Button size="sm" variant="outline" onClick={() => setAdding(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function BankAccountsTab() {
  const { data: accounts = [], isLoading } = useBankAccounts();
  const save = useSaveBankAccount();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    name: '',
    bankName: '',
    accountNumber: '',
    iban: '',
    currency: 'AED',
    openingBalance: '0',
  });
  const [error, setError] = useState<string | null>(null);

  if (isLoading) return <TableSkeleton columns={4} />;

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between pb-3">
        <div>
          <CardTitle className="text-base">Bank accounts</CardTitle>
          <CardDescription>
            Balances are the opening balance plus every recorded movement — never a figure typed in.
            Only the last four digits of an account number are stored.
          </CardDescription>
        </div>
        <Can permission="finance.settings.update">
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" />
            Add account
          </Button>
        </Can>
      </CardHeader>
      <CardContent>
        {accounts.length === 0 && !adding ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No bank accounts yet. Add one so payments and expenses can be banked.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Account</TableHead>
                <TableHead>Number</TableHead>
                <TableHead className="text-end">Opening</TableHead>
                <TableHead className="text-end">Current balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {accounts.map((account) => (
                <TableRow key={account.id}>
                  <TableCell>
                    <div className="font-medium">{account.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {account.bankName ?? '—'} · {account.currency}
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-sm text-muted-foreground">
                    {account.accountNumberMasked ?? '—'}
                  </TableCell>
                  <TableCell className="tabular text-end text-sm text-muted-foreground">
                    {formatMoney(account.openingBalance, account.currency)}
                  </TableCell>
                  <TableCell className="tabular text-end font-semibold">
                    {formatMoney(account.currentBalance, account.currency)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {adding && (
          <div className="mt-4 space-y-3 rounded-md border p-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label>Account name</Label>
                <Input
                  value={form.name}
                  placeholder="Main current account"
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Bank</Label>
                <Input
                  value={form.bankName}
                  onChange={(event) => setForm({ ...form, bankName: event.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Account number</Label>
                <Input
                  value={form.accountNumber}
                  onChange={(event) => setForm({ ...form, accountNumber: event.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>IBAN</Label>
                <Input
                  value={form.iban}
                  onChange={(event) => setForm({ ...form, iban: event.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Currency</Label>
                <Input
                  maxLength={3}
                  value={form.currency}
                  onChange={(event) =>
                    setForm({ ...form, currency: event.target.value.toUpperCase() })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label>Opening balance</Label>
                <Input
                  inputMode="decimal"
                  value={form.openingBalance}
                  onChange={(event) => setForm({ ...form, openingBalance: event.target.value })}
                />
              </div>
            </div>
            <FormError message={error} />
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={save.isPending || form.name.trim().length === 0}
                onClick={async () => {
                  setError(null);
                  try {
                    await save.mutateAsync(form);
                    setAdding(false);
                  } catch (caught) {
                    setError(
                      caught instanceof ApiRequestError ? caught.message : 'Could not save.',
                    );
                  }
                }}
              >
                Save account
              </Button>
              <Button size="sm" variant="outline" onClick={() => setAdding(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ChartAccountsTab() {
  const { data: accounts = [], isLoading } = useChartAccounts();
  const { data: categories = [] } = useExpenseCategories();

  if (isLoading) return <TableSkeleton columns={3} />;

  const byType = accounts.reduce<Record<string, typeof accounts>>((groups, account) => {
    groups[account.type] = [...(groups[account.type] ?? []), account];
    return groups;
  }, {});

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Chart of accounts</CardTitle>
          <CardDescription>
            Accounts marked as system are referenced by the posting rules and cannot be renumbered.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'].map((type) => (
            <div key={type}>
              <h3 className="mb-2 text-sm font-semibold">{type}</h3>
              <ul className="space-y-1">
                {(byType[type] ?? []).map((account) => (
                  <li key={account.id} className="flex items-center gap-3 text-sm">
                    <span className="w-14 font-mono text-xs text-muted-foreground">
                      {account.code}
                    </span>
                    <span className="flex-1">{account.name}</span>
                    {account.isSystem && (
                      <Badge variant="secondary" className="text-[10px]">
                        System
                      </Badge>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Expense categories</CardTitle>
          <CardDescription>Each maps to the account its spending posts to.</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="space-y-1.5">
            {categories.map((category) => (
              <li key={category.id} className="flex items-center justify-between text-sm">
                <span>{category.name}</span>
                <span className="text-xs text-muted-foreground">
                  {category.glAccount
                    ? `${category.glAccount.code} ${category.glAccount.name}`
                    : 'Not mapped'}
                </span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

function VendorsTab() {
  const { data: vendors = [], isLoading } = useVendors();
  const save = useSaveVendor();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', contactName: '', email: '', phone: '', trn: '' });

  if (isLoading) return <TableSkeleton columns={3} />;

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between pb-3">
        <div>
          <CardTitle className="text-base">Vendors</CardTitle>
          <CardDescription>Who the business buys from.</CardDescription>
        </div>
        <Can permission="finance.expense.create">
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" />
            Add vendor
          </Button>
        </Can>
      </CardHeader>
      <CardContent>
        {vendors.length === 0 && !adding ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No vendors yet.</p>
        ) : (
          <ul className="space-y-1.5">
            {vendors.map((vendor) => (
              <li key={vendor.id} className="flex items-center justify-between text-sm">
                <span>
                  <span className="font-medium">{vendor.name}</span>
                  {vendor.contactName && (
                    <span className="ms-2 text-xs text-muted-foreground">
                      {vendor.contactName}
                    </span>
                  )}
                </span>
                <span className="text-xs text-muted-foreground">
                  {vendor.email ?? vendor.phone ?? '—'} · {vendor.paymentTermsDays} days
                </span>
              </li>
            ))}
          </ul>
        )}

        {adding && (
          <div className="mt-4 space-y-3 rounded-md border p-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Contact</Label>
                <Input
                  value={form.contactName}
                  onChange={(event) => setForm({ ...form, contactName: event.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(event) => setForm({ ...form, email: event.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Tax number</Label>
                <Input
                  value={form.trn}
                  onChange={(event) => setForm({ ...form, trn: event.target.value })}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={save.isPending || form.name.trim().length === 0}
                onClick={async () => {
                  await save.mutateAsync(form);
                  setAdding(false);
                  setForm({ name: '', contactName: '', email: '', phone: '', trn: '' });
                }}
              >
                Save vendor
              </Button>
              <Button size="sm" variant="outline" onClick={() => setAdding(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Currencies and live rates.
 *
 * The rates are stored, not fetched per render: an invoice raised this morning
 * must not re-price itself this afternoon because a feed moved. The date each
 * rate was taken is on the row, so nobody has to trust a number blindly.
 */
function CurrenciesTab() {
  const { data: user } = useSession();
  const base = user?.organization.baseCurrency ?? 'AED';

  const { data: rates = [], isLoading } = useExchangeRates(base);
  const refresh = useRefreshExchangeRates();
  const [error, setError] = useState<string | null>(null);

  // Only the currencies this business might actually bill in — the feed
  // carries about a hundred and sixty, and nobody is reading all of them.
  const held = new Map(rates.map((row) => [row.quote, row]));
  const shown = CURRENCIES.filter((currency) => currency.code !== base);
  const lastFetched = rates.length
    ? rates.reduce((latest, row) => (row.fetchedAt > latest ? row.fetchedAt : latest), rates[0].fetchedAt)
    : null;

  const pull = async () => {
    setError(null);
    try {
      const result = await refresh.mutateAsync(base);
      if (result.stored === 0) {
        setError('The rate feed could not be reached. The stored rates are unchanged.');
      }
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not refresh rates.');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Exchange rates</CardTitle>
          <CardDescription>
            Live rates against {base}, your base currency. A client billed in another currency is
            converted at the rate below, and every converted total is reported back in {base}.
            {lastFetched ? ` Last taken ${formatDateTime(lastFetched)}.` : ''}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Can permission="finance.settings.update">
              <Button variant="outline" onClick={pull} disabled={refresh.isPending}>
                <RefreshCw className={refresh.isPending ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
                {refresh.isPending ? 'Fetching…' : 'Refresh rates'}
              </Button>
            </Can>
            <span className="text-xs text-muted-foreground">
              Rates refresh on their own every night. Change the base currency on the Organisation
              page.
            </span>
          </div>

          <FormError message={error} />

          {isLoading ? (
            <TableSkeleton columns={4} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Currency</TableHead>
                  <TableHead>1 {base} buys</TableHead>
                  <TableHead>1 unit costs</TableHead>
                  <TableHead>Taken</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shown.map((currency) => {
                  const row = held.get(currency.code);
                  const rate = row ? Number(row.rate) : null;
                  return (
                    <TableRow key={currency.code}>
                      <TableCell className="font-medium">
                        {currency.code}
                        <span className="ms-2 text-xs text-muted-foreground">{currency.name}</span>
                      </TableCell>
                      <TableCell className="tabular">
                        {rate === null ? '—' : `${rate.toFixed(4)} ${currency.code}`}
                      </TableCell>
                      <TableCell className="tabular">
                        {rate === null || rate === 0 ? '—' : `${(1 / rate).toFixed(4)} ${base}`}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {row ? formatDate(row.fetchedAt) : 'Not fetched yet'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ConverterCard base={base} />
    </div>
  );
}

/** A calculator, so nobody quotes a client from a rate they guessed. */
function ConverterCard({ base }: { base: string }) {
  const [amount, setAmount] = useState('1000');
  const [from, setFrom] = useState(base);
  const [to, setTo] = useState(base === 'USD' ? 'AED' : 'USD');

  const clean = /^\d+(\.\d{1,2})?$/.test(amount) ? amount : '';
  const { data: quote, isFetching, error } = useRateQuote(clean, from, to);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Convert</CardTitle>
        <CardDescription>
          Priced at the stored rate — the same one an invoice in that currency would use.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-[1fr_9rem_9rem]">
          <div className="space-y-1.5">
            <Label>Amount</Label>
            <Input value={amount} onChange={(event) => setAmount(event.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>From</Label>
            <Select value={from} onChange={(event) => setFrom(event.target.value)}>
              {CURRENCIES.map((currency) => (
                <option key={currency.code} value={currency.code}>
                  {currency.code}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>To</Label>
            <Select value={to} onChange={(event) => setTo(event.target.value)}>
              {CURRENCIES.map((currency) => (
                <option key={currency.code} value={currency.code}>
                  {currency.code}
                </option>
              ))}
            </Select>
          </div>
        </div>

        {!clean && <p className="text-sm text-muted-foreground">Enter an amount like 1500 or 1500.50.</p>}
        {from === to && clean && (
          <p className="text-sm text-muted-foreground">Same currency — nothing to convert.</p>
        )}
        {error && (
          <FormError
            message={
              error instanceof ApiRequestError ? error.message : 'No rate held for that pair yet.'
            }
          />
        )}
        {quote && !isFetching && (
          <div className="rounded-md border bg-muted/40 p-3">
            <div className="text-lg font-semibold tabular">
              {quote.converted} {quote.to}
            </div>
            <div className="text-xs text-muted-foreground">
              1 {quote.from} = {quote.rate.toFixed(4)} {quote.to}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
